import { defaults, noop } from 'lodash';
import { apis } from '@doltech/core/lib/api/api.config';
import { isAudio, isImage } from './file';

export const FileTypeEnum = {
  DOC: 'DOC',
  PPT: 'PPT',
  PDF: 'PDF',
  EXCEL: 'EXCEL',
  GIF: 'GIF',
  AUDIO: 'AUDIO',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  SRT: 'SRT',
  VTT: 'VTT',
  OTHER: 'OTHER',
} as const;

export type FileTypeEnum = (typeof FileTypeEnum)[keyof typeof FileTypeEnum];

export const FILE_TYPE_EXTENSION = [
  { type: FileTypeEnum.DOC, extension: ['.doc', '.docx'] },
  { type: FileTypeEnum.PPT, extension: ['.pptx', '.pptm', '.ppt'] },
  { type: FileTypeEnum.PDF, extension: ['.pdf'] },
  { type: FileTypeEnum.EXCEL, extension: ['.xlsx', '.xls'] },
  { type: FileTypeEnum.GIF, extension: ['.gif'] },
  { type: FileTypeEnum.AUDIO, extension: ['.m4a', '.wav', '.wma', '.aac', '.mp3', '.ogg'] },
  { type: FileTypeEnum.IMAGE, extension: ['.jpeg', '.jpg', '.png', '.svg'] },
  { type: FileTypeEnum.VIDEO, extension: ['.mp4', '.mov', '.wmv', '.avi'] },
  { type: FileTypeEnum.SRT, extension: ['.srt'] },
  { type: FileTypeEnum.VTT, extension: ['.vtt'] },
];

export const getFileType = (fileName: string, defaultValue: FileTypeEnum = FileTypeEnum.OTHER) => {
  if (fileName) {
    const index = fileName?.lastIndexOf('.');
    const extenstion = fileName?.substring(index);
    const found = FILE_TYPE_EXTENSION.find((item) => item.extension.includes(extenstion));
    if (found) {
      return found.type;
    }
  }
  return defaultValue;
};

const parseUploadHeader = ({ headers, resourceUrl, contentType, usePrivate }) => {
  const isApiV2 = resourceUrl.includes('X-Amz-Algorithm=AWS4-HMAC-SHA256');

  const newHeaders = {};
  Object.keys(headers)
    .filter((key) => key !== 'Host')
    .forEach((key) => {
      if (headers[key] instanceof Array) {
        newHeaders[key] = headers[key][0];
      } else {
        newHeaders[key] = headers[key];
      }
    });

  if (isApiV2) {
    return defaults(newHeaders, {
      'Content-Type': contentType,
    });
  }
  const now = new Date();

  return defaults(newHeaders, {
    'x-amz-date': now.toUTCString(),
    'Content-Type': contentType,
    'x-amz-acl': usePrivate ? 'private' : 'public-read',
  });
};

const getImageInfo = (imageUrl): any => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => {
      reject(new Error('Cannot load image'));
    };
  });
};

export const handleUploadBlob = async (blob: Blob, fileInfo, options, retryTime = 3) => {
  const { usePrivate, onUploadProgress = noop } = options;
  const now = new Date();
  const resourceUrlPath = `/resource/amz/s3/upload/token${usePrivate ? '/private' : ''}`;
  const fileExt = fileInfo.name.split('.').pop();

  const contentType = fileInfo.type || 'binary/octet-stream';

  const tokenResponse: any = await apis.s3.get(
    resourceUrlPath,
    {
      file_extension: fileExt,
      resource_category: 'MEDIA',
      resource_path: (fileInfo?.name || '').split('.')[0],
    },
    {
      headers: {
        'x-amz-date': now.toUTCString(),
        'AWS-Content-Type': contentType,
      },
    }
  );
  if (tokenResponse.status === 401 && retryTime > 0) {
    return handleUploadBlob(blob, fileInfo, options, retryTime - 1);
  }
  if (tokenResponse.status === 200 && tokenResponse.data) {
    let firstLoaded = 0;
    if (isAudio(fileInfo)) {
      const needConvert = fileInfo.type !== 'audio/mpeg';
      if (needConvert) {
        const form = new FormData();
        form.append('file', fileInfo);
        const convertedAudio: any = await apis.googleCloudAudio.post('/convert', form, {
          params: {
            output: 'mp3',
          },
          maxBodyLength: Infinity,
          responseType: 'blob',
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            firstLoaded = loaded;
            onUploadProgress({ ...progressEvent, loaded: firstLoaded / 10, total });
          },
        });
        blob = convertedAudio.data;
      }
    }

    const uploadHeaders = parseUploadHeader({
      headers: tokenResponse.data.uploadHeaders,
      resourceUrl: tokenResponse.data.resourceUrl,
      contentType,
      usePrivate,
    });
    const uploadResponse = await apis.noAuth.put(tokenResponse.data.resourceUrl, blob, {
      headers: uploadHeaders,
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        if (firstLoaded > 0) {
          onUploadProgress({ ...progressEvent, loaded: Math.max(loaded, total / 10), total });
        } else {
          onUploadProgress(progressEvent);
        }
      },
      timeout: 70000,
    });
    if (uploadResponse.status === 200) {
      let result = {
        tokenResponse: tokenResponse.data,
        file: {
          url: tokenResponse?.data?.downloadResourceUrl,
          path: tokenResponse?.data?.path,
          fileType: usePrivate ? 'PRIVATE' : 'PUBLIC',
          ...fileInfo,
          name: fileInfo.name,
          size: fileInfo.size,
          uploadedDate: now.toISOString(),
        },
      } as any;
      if (isImage(fileInfo)) {
        try {
          const { width, height } = await getImageInfo(tokenResponse?.data?.downloadResourceUrl);

          result = {
            ...result,
            file: {
              ...result.file,
              width,
              height,
            },
            width,
            height,
          } as any;
        } catch (err) {
          // Set default value width and height if fail to load image
          result = {
            ...result,
            file: {
              ...result.file,
              width: 300,
              height: 300,
            },
            width: 300,
            height: 300,
          } as any;
        }
      }
      return result;
    }
  } else {
    throw new Error('Failed to upload');
  }
  return null;
};
